import { useState } from "react";
import { GridApi } from "ag-grid-community";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import ConfirmationModal from "./ConfirmationModal";

interface GridEditActionsProps<T extends { id: number }> {
    pendingCount: number;
    pending: Record<number, Partial<T>>;
    gridRef: React.MutableRefObject<GridApi | undefined>;
    resetBaseline: (rows: T[]) => void;

    baselineData: T[];

    getBaselineRows?: () => T[];

    isActiveToggle?: boolean;
    setRowData: React.Dispatch<React.SetStateAction<T[]>>;
    setPending: React.Dispatch<React.SetStateAction<Record<number, Partial<T>>>>;
    onSave: () => Promise<void>;
}

export default function GridEditActions<T extends { id: number }>({
                                                                      pendingCount,
                                                                      gridRef,
                                                                      resetBaseline,
                                                                      baselineData,
                                                                      getBaselineRows,
                                                                      isActiveToggle,
                                                                      setRowData,
                                                                      setPending,
                                                                      onSave,
                                                                  }: GridEditActionsProps<T>) {
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);

    const doDiscard = () => {
        gridRef.current?.stopEditing();
        setPending({});

        const rows = getBaselineRows ? getBaselineRows() : baselineData;
        const baselineById = new Map(rows.map(r => [r.id, r]));

        setRowData(prev =>
            prev.map(r => {
                const b = baselineById.get(r.id);
                return b ? { ...r, ...b } : r;
            })
        );
        resetBaseline(rows);

        setTimeout(() => {
            gridRef.current?.refreshCells({ force: true });
            gridRef.current?.refreshClientSideRowModel("filter");
        }, 0);
    };

    const handleDiscardClick = () => {
        gridRef.current?.stopEditing();
        setShowDiscardModal(true);
    };

    const handleConfirmDiscard = () => {
        doDiscard();
    };

    const handleSaveClick = () => {
        gridRef.current?.stopEditing();
        setShowSaveModal(true);
    };

    const handleConfirmSave = async () => {
        await onSave();
    };

    if (pendingCount === 0) {
        return null;
    }

    return (
        <>
            <SecondaryButton onClick={handleDiscardClick}>
                Discard ({pendingCount})
            </SecondaryButton>

            <PrimaryButton onClick={handleSaveClick}>
                Save ({pendingCount})
            </PrimaryButton>

            <ConfirmationModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onConfirm={handleConfirmSave}
                pendingCount={pendingCount}
            />

            <ConfirmationModal
                isOpen={showDiscardModal}
                onClose={() => setShowDiscardModal(false)}
                onConfirm={handleConfirmDiscard}
                pendingCount={pendingCount}
                mode="discard"
            />
        </>
    );
}
